import { Column, Row } from '@shipt/design-system-layouts'
import { Body } from '@shipt/design-system-typography'
import { css } from '@/features/shared/styles'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { trackAvailableStoreViewed } from '@/features/shared/analytics/selectStore'
import { type MouseEvent } from 'react'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { type ShoppingStoreDetails } from '@/features/shop/services/ShoppingStore/types'
import { TextButtonLink, IconButtonLink } from '@/features/shared/elements/Link/Buttons'
import { type RetailerShelfProps } from '@/features/shop/components/GlobalSearchPage/RetailerShelves'
import { screenSizes } from '@shipt/design-system-themes'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import {
  type RichTextElement,
  type CMSProductShelfInnerData,
} from '@/features/cms/components/CMS/types'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import { ArrowRightIcon } from '@shipt/design-system-icons'

type Props = {
  store: ShoppingStoreDetails
  index: number
  handleStoreClick?: RetailerShelfProps['handleStoreClick']
  showPricing?: boolean
  interactiveImage?: boolean
  customHeading?: string
  subheading?: RichTextElement[]
  imageSize?: number
  src?: string
  data?: CMSProductShelfInnerData
  astroDlsTheme?: boolean
  viewAllHref?: string
}

const BrandAmplificationButtons = ({
  storeName,
  handleStoreClick,
  viewAllHref,
}: {
  storeName: string
  handleStoreClick: () => void
  viewAllHref: string
}) => {
  const commonTextProps = {
    'aria-label': `View all for ${storeName}`,
    children: 'View all',
  } as const

  const commonIconProps = {
    'aria-label': `View all for ${storeName}`,
    variant: 'tertiary',
    icon: ArrowRightIcon,
    size: 'sm',
  } as const

  const commonLinkProps = {
    href: viewAllHref,
    onClick: (e: MouseEvent) => {
      e.preventDefault()
      handleStoreClick()
    },
  } as const

  return (
    <>
      <Row display={{ base: 'none', md: 'flex' }}>
        <ViewAllTextButtonLink {...commonTextProps} {...commonLinkProps} />
      </Row>
      <Row display={{ base: 'flex', md: 'none' }} align="center">
        <ViewAllIconButtonLink {...commonIconProps} {...commonLinkProps} />
      </Row>
    </>
  )
}

const ViewAllControl = ({
  store,
  index,
  handleStoreClick,
  astroDlsTheme,
  viewAllHref,
}: {
  store: ShoppingStoreDetails
  index: number
  handleStoreClick: NonNullable<Props['handleStoreClick']>
  astroDlsTheme?: boolean
  viewAllHref: string
}) => {
  if (astroDlsTheme) {
    return (
      <BrandAmplificationButtons
        storeName={store.name}
        handleStoreClick={() =>
          handleStoreClick(store, index, 'view_all_quicklink', 'view_all_quick_link')
        }
        viewAllHref={viewAllHref}
      />
    )
  }

  return (
    <ViewAllButtonWrapperLink
      href={viewAllHref}
      onClick={(e: MouseEvent) => {
        e.preventDefault()
        handleStoreClick(store, index, 'view_all_quicklink', 'view_all_quick_link')
      }}
      aria-label={`View all for ${store.name}`}
    >
      View all
    </ViewAllButtonWrapperLink>
  )
}

export const RetailerShelfHeading = ({
  store,
  index,
  handleStoreClick,
  showPricing = true,
  imageSize = 40,
  customHeading,
  interactiveImage = true,
  subheading,
  src,
  data,
  astroDlsTheme,
  viewAllHref,
}: Props) => {
  const logoBackgroundColor =
    data?.store_logo?.data.background_color ?? data?.store_card?.data.background_color ?? ''
  const borderColor = astroDlsTheme ? '#0000001a' : 'white'

  const StoreImage = astroDlsTheme ? (
    <RectangularImage
      key={store.name}
      src={src || store.circular_image}
      alt="rectangular store logo"
      width={56}
      height={40}
    />
  ) : (
    <CircularImage
      key={store.name}
      src={src ?? store.circular_image}
      alt="store logo"
      width={imageSize}
      height={imageSize}
    />
  )

  return (
    <CarouselTitle>
      <ScrollIntoView
        onInView={() =>
          trackAvailableStoreViewed({
            storeId: store.id,
            storeName: store.name,
            index,
          })
        }
      >
        <RetailerLogoAndName>
          <RetailerLogo
            data-retailer-logo
            logoBackgroundColor={logoBackgroundColor}
            borderColor={borderColor}
          >
            {interactiveImage && handleStoreClick ? (
              <TransparentButton onClick={() => handleStoreClick(store, index, 'store_logo')}>
                {StoreImage}
              </TransparentButton>
            ) : (
              StoreImage
            )}
          </RetailerLogo>
          <Column>
            <Row spacing="sm" align="center">
              <Body strong size="xl">
                {customHeading ?? store.name}
              </Body>
              {!!showPricing && (
                <Body size="md" variant="secondary">
                  No markups
                </Body>
              )}
            </Row>
            {!!subheading && <Subheading>{subheading.map(renderElement)} </Subheading>}
          </Column>
        </RetailerLogoAndName>
      </ScrollIntoView>
      {!!handleStoreClick && !!viewAllHref && (
        <ViewAllControl
          store={store}
          index={index}
          handleStoreClick={handleStoreClick}
          astroDlsTheme={astroDlsTheme}
          viewAllHref={viewAllHref}
        />
      )}
    </CarouselTitle>
  )
}

const Subheading = styled.div`
  *,
  & {
    color: ${({ theme }) => theme.color.content.base.neutral.secondary};
  }
`

const viewAllTextStyles = css`
  width: 4.375rem;
`

const viewAllIconStyles = css`
  height: 2rem;
  margin-right: 1.25rem;
`

const viewAllButtonWrapperStyles = css`
  background-color: transparent;
  padding: 0;
  border: none;
  flex-shrink: 0;
  text-decoration: underline;

  @media ${screenSizes.max_mobile} {
    padding-right: 16px;
  }
`

const ViewAllTextButtonLink = styled(TextButtonLink)`
  ${viewAllTextStyles}
`

const ViewAllIconButtonLink = styled(IconButtonLink)`
  ${viewAllIconStyles}
`

const ViewAllButtonWrapperLink = styled(TextButtonLink)`
  ${viewAllButtonWrapperStyles}
`

const CircularImage = styled(Image)`
  border-radius: 50%;
`

const CarouselTitle = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`

const RetailerLogoAndName = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;

  @media ${screenSizes.max_mobile} {
    padding-left: 16px;
  }
`

const RetailerLogo = styled.div<{
  logoBackgroundColor: string
  borderColor: string
}>`
  background-color: ${({ logoBackgroundColor }) => logoBackgroundColor};
  border-radius: 8px;
  border: 1px solid ${({ borderColor }) => borderColor};
  display: flex;
`

const RectangularImage = styled(Image)`
  object-fit: contain;
`
